import React from 'react';
import GuideLayout from '../common/GuideLayout';
import GuideCta from '../common/GuideCta';
import TemplateCard from '../common/TemplateCard';
import RelatedGuides from '../common/RelatedGuides';
import { relatedTo } from '../common/rsvpCluster';
import { SITE_SIGNUP_URL } from '../signup/startIntent';
import shotEvenements from '../../../assets/rsvp-2-evenements.webp';
import shotMenu from '../../../assets/rsvp-3-menu.webp';
import { AVOID, CARDS, EXAMPLE, FAQ, MUST, READING_MINUTES, TOC, UPDATED, USEFUL } from './content';

// Page « Formulaire de réponse mariage » : les questions à poser, un exemple,
// la version papier (carton réponse) ; appels à créer son formulaire en ligne.
const FormGuidePage: React.FC = () => (
  <GuideLayout
    eyebrow="Guide · Invitations"
    title="Formulaire de réponse mariage : les questions à poser à vos invités"
    updated={UPDATED}
    published="2026-10-15"
    readingMinutes={READING_MINUTES}
    lead="Que demander à vos invités quand ils répondent à votre faire-part ? Les questions indispensables, celles qui vous simplifieront la vie, ce qu’il vaut mieux éviter, un exemple de formulaire complet et des modèles de carton réponse."
    topCta={
      <GuideCta
        to={SITE_SIGNUP_URL}
        title="Votre formulaire de réponse en ligne, gratuit"
        text="Présence par événement, menu, allergies, réponse pour toute la famille : tout est prêt, il ne reste qu’à choisir vos questions."
        label="Créer mon formulaire gratuit"
      />
    }
    toc={TOC}
    faq={FAQ}
  >
    <section id="indispensables">
      <h2>Les questions indispensables</h2>
      <p>Trois informations suffisent pour compter vos invités et faire votre plan de table :</p>
      <div className="mg-grid">
        {MUST.map((m) => (
          <div key={m.title} className="mg-card">
            <h3>{m.title}</h3>
            <p>{m.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section id="utiles">
      <h2>Les questions utiles</h2>
      <p>Selon votre mariage, ajoutez celles qui vous éviteront des allers-retours plus tard :</p>
      <div className="mg-grid">
        {USEFUL.map((u) => (
          <div key={u.title} className="mg-card">
            <h3>{u.title}</h3>
            <p>{u.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section id="eviter">
      <h2>Ce qu’il vaut mieux éviter</h2>
      <ul className="mg-bullets">
        {AVOID.map((a) => (
          <li key={a}>{a}</li>
        ))}
      </ul>
    </section>

    <section id="exemple">
      <h2>Exemple de formulaire de réponse de mariage</h2>
      <p>Un formulaire complet, qui se remplit en deux minutes :</p>
      <div className="mg-example-form">
        {EXAMPLE.map((e, i) => (
          <div key={e.q} className="mg-example-row">
            <span className="mg-example-num">{i + 1}</span>
            <b>{e.q}</b>
            <span>{e.a}</span>
          </div>
        ))}
      </div>
      <div className="mg-phones mg-phones-2">
        <figure>
          <img
            src={shotEvenements}
            width={420}
            height={909}
            loading="lazy"
            decoding="async"
            alt="Formulaire de réponse : présence événement par événement"
          />
          <figcaption>Une réponse par événement</figcaption>
        </figure>
        <figure>
          <img
            src={shotMenu}
            width={420}
            height={909}
            loading="lazy"
            decoding="async"
            alt="Formulaire de réponse : choix du menu, allergies et questions personnalisées"
          />
          <figcaption>Le menu, les allergies, vos questions</figcaption>
        </figure>
      </div>
      <GuideCta
        to={SITE_SIGNUP_URL}
        title="Ce formulaire, prêt en 2 minutes"
        text="Vos invités choisissent leur nom dans votre liste, répondent pour chaque événement et pour toute leur famille. Les réponses arrivent directement dans votre liste d’invités."
        label="Créer mon formulaire gratuit"
      />
    </section>

    <section id="carton">
      <h2>La version papier : le carton réponse</h2>
      <p>
        Le carton réponse est le formulaire traditionnel : une petite carte glissée dans l’enveloppe du faire-part,
        souvent au format carte postale, que l’invité remplit et vous renvoie. Il a son charme, mais demande une
        enveloppe timbrée, arrive au compte-gouttes et doit être recopié à la main.
      </p>
      <p>
        Le bon compromis : un carton avec un QR code qui mène au formulaire en ligne. Trois modèles à copier, en
        remplaçant ce qui est entre crochets :
      </p>
      <div className="mg-templates">
        {CARDS.map((c) => (
          <TemplateCard key={c.title} template={c} />
        ))}
      </div>
    </section>

    <RelatedGuides items={relatedTo('form')} />
  </GuideLayout>
);

export default FormGuidePage;
